import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  api,
  downloadFile,
  type Analytics,
  type AnalyticsBucket,
  type AnalyticsKpis,
  type AnalyticsPeriod,
} from "../api";
import { useBranch } from "../lib/use-branch";
import { useFeature } from "../lib/use-tariff";
import { cn } from "../lib/utils";
import { ExportButton } from "../components/export-button";
import { InfoTooltip } from "../components/info-tooltip";
import { FeatureGate } from "../components/feature-gate";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BranchComparisonCard } from "../components/analytics/branch-comparison-card";
import { EmployeesCard } from "../components/analytics/employees-card";
import { EngagementCard } from "../components/analytics/engagement-card";
import { HeatmapCard } from "../components/analytics/heatmap-card";
import { InsightStrip } from "../components/analytics/insight-strip";
import type { KpiItem } from "../components/analytics/kpi-card";
import { KpiGrid, KpiGridSkeleton } from "../components/analytics/kpi-grid";
import {
  KPI_DESCRIPTORS,
  subLabel,
} from "../components/analytics/kpi-descriptors";
import { KpiTableCard } from "../components/analytics/kpi-table-card";
import { NewClientsTrendCard } from "../components/analytics/new-clients-trend-card";
import { RevenueChartCard } from "../components/analytics/revenue-chart-card";
import { TopClientsCard } from "../components/analytics/top-clients-card";
import { TopServicesCard } from "../components/analytics/top-services-card";
import { TovarTahliliPage } from "./TovarTahliliPage";
import type { OmborJump } from "./OmborPage";

const PERIOD_VALUES: AnalyticsPeriod[] = ["day", "week", "month", "quarter", "year"];

/**
 * Davrga mos guruhlash variantlari.
 *
 * "Bugun" — faqat soat kesimi: kun/hafta/oy kesimi bir qator berardi, bundan
 * tashqari kun bucket kalitlari UTC sanasidan olingani uchun mahalliy kun ikki
 * UTC sanasiga tushib, biri bo'sh qator bo'lib chiqardi. Yilni soatlab
 * guruhlash esa ~8700 qator (backend ham `group_by_too_granular` bilan rad
 * etadi).
 */
function groupByOptionsFor(period: AnalyticsPeriod): AnalyticsBucket[] {
  if (period === "day") return ["hour"];
  return ["day", "week", "month"];
}

interface Props {
  onOpenReminders?: () => void;
  onOpenTariff: () => void;
  /** Signallar qatoridagi amal — Ombor sahifasiga payload bilan o'tish. */
  onOmborJump: (jump: OmborJump) => void;
}

export function AnalyticsPage({ onOpenReminders, onOpenTariff, onOmborJump }: Props) {
  return (
    <FeatureGate feature="analytics" requiredTariff="pro" onUpgrade={onOpenTariff}>
      <AnalyticsInner onOpenReminders={onOpenReminders} onOmborJump={onOmborJump} />
    </FeatureGate>
  );
}

/** Sahifaning ichki ko'rinishlari: umumiy tahlil yoki tovar tahlili. */
type AnalyticsView = "umumiy" | "tovar";

/** Bo'lim sarlavhasi — sahifaning ierarxiyasini ko'rinadigan qiladi. */
function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-center gap-1.5 pt-1">
      <h2 className="text-sm font-semibold">{title}</h2>
      {hint && <InfoTooltip text={hint} />}
    </div>
  );
}

/**
 * Tahlil sahifasi — "Qanday ketyapti?" → "Nega?" → "Qayerda?" → "Tafsilot".
 *
 * Bo'limlar tartibi ATAYIN: avval KPI to'ri (bir qarashda holat), keyin
 * insaytlar, keyin daromad grafigi + uning drayverlari (tushuntirish), so'ng
 * xizmat/mijoz/jamoa kesimlari, oxirida ikkinchi darajali ma'lumot va
 * yig'iladigan KPI jadvali.
 *
 * Ikkita so'rov: og'ir `/owner/analytics` (heatmap, seriyalar, top-5 lar) va
 * yengil `/owner/analytics/kpis` (guruhlash almashganda faqat shu qayta
 * tortiladi). Redizayn HECH QANDAY yangi so'rov qo'shmaydi.
 */
function AnalyticsInner({
  onOpenReminders,
  onOmborJump,
}: {
  onOpenReminders?: () => void;
  onOmborJump: (jump: OmborJump) => void;
}) {
  const { t } = useTranslation(["analytics", "common"]);
  const { selectedBranchId } = useBranch();
  const hasComparison = useFeature("analytics_branch");
  // Omborsiz tarifda (comfort) tovar/qaytarish KPI'lari doim nol bo'lardi —
  // ularni ko'rsatish o'rniga butunlay olib tashlaymiz. Comfort'da analitika
  // BOR, faqat ombor yo'q (`_COMFORT = _PRO - {PRODUCTS, BRANCHES}`).
  const hasProducts = useFeature("tovars");
  const [view, setView] = useState<AnalyticsView>("umumiy");
  const [period, setPeriod] = useState<AnalyticsPeriod>("month");
  const [data, setData] = useState<Analytics | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [groupBy, setGroupBy] = useState<AnalyticsBucket>("day");
  const [kpis, setKpis] = useState<AnalyticsKpis | null>(null);
  const [kpisLoading, setKpisLoading] = useState(false);

  const descriptors = useMemo(
    () => KPI_DESCRIPTORS.filter((d) => hasProducts || !d.needsProducts),
    [hasProducts],
  );

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api
      .analytics(period, selectedBranchId)
      .then(setData)
      .catch((e) =>
        setErr((e as { message?: string }).message || t("common:common.error")),
      )
      .finally(() => setLoading(false));
  }, [period, selectedBranchId]);

  // Davr almashtirilganda guruhlash shu davrning tabiiy darajasiga qaytadi:
  // "Bugun" → soat, "Yil" → oy. Egasi keyin xohlagan kesimni tanlashi mumkin
  // va u davr o'zgarmaguncha saqlanadi (shuning uchun `groupBy` dep'da yo'q —
  // aks holda tanlov darhol qaytarib tashlanardi).
  const lastPeriod = useRef(period);
  useEffect(() => {
    if (lastPeriod.current === period) return;
    lastPeriod.current = period;
    setGroupBy(groupByOptionsFor(period)[0]);
  }, [period]);

  // KPI'lar ALOHIDA so'rovda: guruhlashni almashtirganda og'ir analitika
  // yukini (heatmap, ikki series, top-5 ro'yxatlari) qayta tortmaymiz.
  //
  // Qorovul MAJBURIY: davr o'zgargan-u, yuqoridagi tiklash effekti hali
  // ishlamagan bitta render'da `period=year&group_by=hour` ketib, backend
  // `group_by_too_granular` bilan rad etardi.
  useEffect(() => {
    if (!groupByOptionsFor(period).includes(groupBy)) return;
    setKpisLoading(true);
    api
      .analyticsKpis(period, groupBy, selectedBranchId)
      .then(setKpis)
      .catch(() => setKpis(null))
      .finally(() => setKpisLoading(false));
  }, [period, groupBy, selectedBranchId]);

  // Kartalar — har bir KPI o'zining bucket qatorlaridan sparkline oladi.
  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!kpis) return [];
    return descriptors.map((d) => ({
      key: d.key,
      label: t(`kpi.${d.labelKey}`),
      value: d.format(kpis.current[d.key]),
      sub: subLabel(d, kpis.current),
      pct: kpis.change_pct[d.key] ?? null,
      spark: kpis.buckets.map((b) => ({ value: Number(b.metrics[d.key]) })),
      color: d.color,
      higherIsBetter: d.higherIsBetter,
      hint: d.hintKey ? <InfoTooltip text={t(`kpi.hints.${d.hintKey}`)} /> : undefined,
    }));
  }, [kpis, descriptors, t]);

  const exportComparison = async () => {
    await downloadFile(
      `/owner/analytics/comparison/export.pdf?period=${period}`,
      t("export.filename"),
    );
  };

  return (
    <Tabs
      value={view}
      onValueChange={(v) => setView(v as AnalyticsView)}
      className="analytics-page space-y-4"
    >
      {/* Bitta sarlavha qatori: ko'rinish tablari (chapda) + davr tablari va
          eksport (o'ngda). Omborsiz tarifda (comfort) Tovar tahlili tabi YO'Q —
          bitta trigger'li tab qatori shovqin, shuning uchun ko'rinish tablari
          butunlay yashiriladi (`view` "umumiy"da qoladi) va davr qatori chapda
          turadi. Nested FeatureGate EMAS: analytics qorovuli allaqachon
          tashqarida, `tovars` esa faqat ko'rinishni boshqaradi. Davr tanlovi
          ikkala tabga ham tegishli (tovar sotuv jadvali ham shu davrni oladi);
          eksport esa filiallararo taqqoslash PDF'i — faqat Umumiy'da ma'noli. */}
      <div className="analytics-toolbar flex items-center gap-2 flex-wrap">
        {hasProducts && (
          <TabsList>
            <TabsTrigger value="umumiy">{t("tabs.umumiy")}</TabsTrigger>
            <TabsTrigger value="tovar">{t("tabs.tovar")}</TabsTrigger>
          </TabsList>
        )}
        <div className={cn("flex items-center gap-2 flex-wrap", hasProducts && "ml-auto")}>
          <Tabs value={period} onValueChange={(v) => setPeriod(v as AnalyticsPeriod)}>
            <TabsList className="overflow-x-auto">
              {PERIOD_VALUES.map((p) => (
                <TabsTrigger key={p} value={p}>
                  {t(`periods.${p}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {view === "umumiy" && (
            <ExportButton onExport={exportComparison} label={t("export.label")} />
          )}
        </div>
      </div>

      <TabsContent value="umumiy" className="mt-0 space-y-5">
        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3 text-sm">
            {err}
          </div>
        )}

        {/* ===== 1. Qanday ketyapti — asosiy ko'rsatkichlar ===== */}
        <section className="space-y-2">
          <SectionHeading title={t("sections.kpi")} />
          {kpisLoading && !kpis ? (
            <KpiGridSkeleton count={descriptors.length} />
          ) : (
            <KpiGrid items={kpiItems} />
          )}
        </section>

        {/* ===== 2. E'tibor berish kerak — qoida ishlagandagina chiziladi ===== */}
        <InsightStrip
          kpis={kpis}
          analytics={data}
          hasProducts={hasProducts}
          period={period}
        />

        {/* ===== 3. Nega — daromad va uning drayverlari ===== */}
        <section className="space-y-2">
          <SectionHeading title={t("sections.revenue")} />
          {loading && !data ? (
            <Skeleton className="h-[340px]" />
          ) : data ? (
            <RevenueChartCard
              revenue={data.revenue}
              period={period}
              kpis={kpis}
              hasProducts={hasProducts}
            />
          ) : null}
        </section>

        {data && (
          <>
            {/* ===== 4. Qayerda — sotuv va xizmatlar ===== */}
            <section className="space-y-2">
              <SectionHeading title={t("sections.sales")} />
              <TopServicesCard services={data.services} />
            </section>

            {/* ===== 5. Mijozlar ===== */}
            <section className="space-y-2">
              <SectionHeading title={t("sections.clients")} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <NewClientsTrendCard
                  kpis={kpis}
                  clients={data.clients}
                  loading={kpisLoading}
                />
                <TopClientsCard rows={data.top_clients} />
              </div>
            </section>

            {/* ===== 6. Jamoa va ish yuklamasi ===== */}
            <section className="space-y-2">
              <SectionHeading title={t("sections.team")} />
              <EmployeesCard rows={data.employees} />
              <HeatmapCard cells={data.heatmap} />
            </section>

            {/* ===== 7. Mijozlar bilan ishlash — ikkinchi darajali ===== */}
            <section className="space-y-2">
              <SectionHeading title={t("sections.engagement")} />
              <EngagementCard
                clients={data.clients}
                reminders={data.reminders}
                funnel={data.reminder_funnel}
                onOpenReminders={onOpenReminders}
              />
            </section>
          </>
        )}

        {/* ===== 8. Filiallararo taqqoslash — faqat "Barcha filiallar"da ===== */}
        {selectedBranchId == null && (
          <BranchComparisonCard period={period} enabled={hasComparison} />
        )}

        {/* ===== 9. Tafsilot — yig'iladigan KPI jadvali ===== */}
        <KpiTableCard
          data={kpis}
          descriptors={descriptors}
          groupBy={groupBy}
          options={groupByOptionsFor(period)}
          onGroupBy={setGroupBy}
          loading={kpisLoading}
        />
      </TabsContent>

      {/* `TabsContent` nofaol holatda null qaytaradi — TovarTahliliPage faqat
          tab ochilganda mount bo'lib, sotuv tahlili + signallarni o'shanda
          so'raydi. */}
      <TabsContent value="tovar" className="mt-0">
        <TovarTahliliPage period={period} onJump={onOmborJump} />
      </TabsContent>
    </Tabs>
  );
}
